import React from "react";
import { motion } from "framer-motion";
import { FiShield, FiLock, FiEye, FiInfo, FiUserCheck, FiGlobe, FiAlertCircle, FiMail } from "react-icons/fi";
import Footer from "./Footer";

const Privacypolicy = () => {
  const colors = {
    bg: '#000000',
    accent: '#22c55e',
    card: 'rgba(255, 255, 255, 0.03)',
    border: 'rgba(255, 255, 255, 0.08)',
    text: '#ffffff',
    muted: 'rgba(255, 255, 255, 0.5)',
    glow: 'rgba(34, 197, 94, 0.15)'
  };

  const sections = [
    {
      icon: <FiInfo />,
      title: "Information We Collect",
      content: "We collect personal and non-personal information to enhance your experience. Personal data includes your name and email provided during registration. Non-personal data includes device type, browser, and IP address collected via cookies to optimize performance."
    },
    {
      icon: <FiUserCheck />,
      title: "How We Use Information",
      content: "Your data helps us improve services, communicate updates, and ensure platform security. By analyzing usage patterns, we can better serve our community and deliver more relevant educational content."
    },
    {
      icon: <FiLock />,
      title: "Sharing & Disclosure",
      content: "We do not sell your personal information. Data may be shared with trusted service providers who assist in operations, provided they maintain confidentiality, or when required by law to protect user safety."
    },
    {
      icon: <FiShield />,
      title: "Data Security",
      content: "We implement robust encryption and security measures to prevent unauthorized access. While we strive for absolute protection, no digital storage is 100% secure, so we encourage users to maintain account security."
    },
    {
      icon: <FiEye />,
      title: "Your Rights",
      content: "You have the right to access, update, or request deletion of your data. You can manage cookie preferences through browser settings and opt-out of promotional communications at any time."
    },
    {
      icon: <FiGlobe />,
      title: "Third-Party Links",
      content: "Our platform may contain links to external sites. We are not responsible for their privacy practices. We recommend reviewing their policies before sharing any information with third-party services."
    },
    {
      icon: <FiAlertCircle />,
      title: "Children’s Privacy",
      content: "SnapED codeCampus is not intended for individuals under 13. We do not knowingly collect data from children. If detected, we will take immediate steps to remove such information from our records."
    },
    {
      icon: <FiMail />,
      title: "Contact Us",
      content: "For any privacy-related inquiries, please reach out to us at snaped4338@gmail.com. We are committed to addressing your concerns promptly."
    }
  ];

  return (
    <div style={{ background: colors.bg, minHeight: '100vh', color: '#fff' }}>
      {/* Hero Section */}
      <section style={{
        padding: '160px 20px 80px',
        position: 'relative',
        textAlign: 'center',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)',
          width: '800px', height: '600px',
          background: `radial-gradient(circle, ${colors.glow} 0%, transparent 70%)`,
          filter: 'blur(100px)', pointerEvents: 'none', zIndex: 0
        }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: '800px', margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              width: '64px', height: '64px',
              background: 'rgba(34,197,94,0.1)',
              borderRadius: '20px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: colors.accent, margin: '0 auto 32px',
              border: `1px solid ${colors.border}`
            }}
          >
            <FiShield size={32} />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
              fontWeight: 900,
              letterSpacing: '-0.04em',
              lineHeight: 1,
              marginBottom: '24px'
            }}
          >
            Privacy <span style={{ color: colors.accent }}>Policy</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{ fontSize: '18px', color: colors.muted, lineHeight: 1.6 }}
          >
            At SnapED codeCampus, we prioritize your data privacy and security.
            This policy outlines how we handle your information with transparency and care.
          </motion.p>
        </div>
      </section>

      {/* Content Grid */}
      <section style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px 120px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '24px'
        }}>
          {sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              style={{
                background: colors.card,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${colors.border}`,
                borderRadius: '24px',
                padding: '32px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                transition: 'all 0.3s ease'
              }}
            >
              <div style={{ color: colors.accent, fontSize: '24px' }}>
                {section.icon}
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>{section.title}</h3>
              <p style={{ fontSize: '15px', color: colors.muted, lineHeight: 1.7, margin: 0 }}>
                {section.content}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Last Updated */}
        <div style={{ textAlign: 'center', marginTop: '80px', color: colors.muted, fontSize: '14px' }}>
          Last Updated: March 2026 • © 2026 SnapED codeCampus. All rights reserved.
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Privacypolicy;